// Marketing engine admin dashboard (6 tabs). Self-contained; uses the host
// app's shadcn/Tailwind tokens (bg-card, text-primary, border-border, etc.).
// Mount at /admin/marketing. Auth is enforced by the backend
// require_admin dependency; on 401 we show a sign-in prompt.
import { useEffect, useState, useCallback } from "react";
import {
  LayoutGrid, Newspaper, Sparkles, Send, Globe, Mail, ArrowLeft,
  RefreshCw, AlertTriangle, Play, Copy, Download,
} from "lucide-react";
import marketingApi, { MARKETING_API_BASE } from "../../lib/marketingApi";

const LOGIN_PATH = "/auth";

const TABS = [
  ["overview", "Overview", LayoutGrid],
  ["discovery", "Discovery", Newspaper],
  ["content", "Content", Sparkles],
  ["publishing", "Publishing", Send],
  ["seo", "SEO Pages", Globe],
  ["newsletter", "Newsletter", Mail],
];

export default function MarketingDashboard() {
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState("");
  const [gate, setGate] = useState("checking");

  useEffect(() => {
    marketingApi
      .get("/stats")
      .then(() => setGate("ok"))
      .catch((e) => setGate(e.response?.status === 401 || e.response?.status === 403 ? "unauth" : "ok"));
  }, []);

  const trigger = useCallback(async (job) => {
    setBusy(job);
    setToast(`Starting ${job}...`);
    try {
      const { data: j } = await marketingApi.post(`/admin/run?job=${job}`);
      if (j.jobId) {
        for (let i = 0; i < 150; i++) {
          await new Promise((res) => setTimeout(res, 2000));
          const { data: jr } = await marketingApi.get(`/admin/jobs/${j.jobId}`);
          if (jr.progress?.message) setToast(jr.progress.message);
          if (jr.status === "success") { setToast(`${job} done.`); break; }
          if (jr.status === "error") { setToast(`${job} failed: ${jr.error}`); break; }
        }
      } else if (j.error) {
        setToast(`${job} failed: ${j.error}`);
      } else {
        setToast(`${job} done.`);
      }
    } catch (e) {
      setToast(`${job} failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(null);
      setTimeout(() => setToast(""), 5000);
    }
  }, []);

  if (gate === "unauth") {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-foreground" data-testid="marketing-unauth">
        <div className="text-lg font-semibold">Admin sign-in required</div>
        <a href={LOGIN_PATH} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm">Go to login</a>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background text-foreground" data-testid="marketing-dashboard">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-5 py-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <a href="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Home
            </a>
            <h1 className="text-xl font-semibold">Marketing Engine</h1>
          </div>
        </div>
        <nav className="container mx-auto px-5 flex gap-1 overflow-x-auto pb-1">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`px-3 py-2 text-sm inline-flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${
                tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </header>

      <div className="container mx-auto px-5 py-6">
        {tab === "overview" && <Overview trigger={trigger} busy={busy} />}
        {tab === "discovery" && <Discovery busy={busy} />}
        {tab === "content" && <Content busy={busy} />}
        {tab === "publishing" && <Publishing trigger={trigger} busy={busy} />}
        {tab === "seo" && <Seo busy={busy} />}
        {tab === "newsletter" && <Newsletter busy={busy} />}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-card border border-border rounded-lg shadow-lg px-4 py-3 text-sm flex items-center gap-2 max-w-md z-50" data-testid="job-toast">
          <RefreshCw className="w-4 h-4 animate-spin" /> {toast}
        </div>
      )}
    </main>
  );
}

function RunBtn({ label, job, trigger, busy, icon: Icon = Play, variant = "default" }) {
  const isBusy = busy === job;
  const baseClass = variant === "primary"
    ? "bg-primary text-primary-foreground hover:opacity-90"
    : "bg-secondary text-secondary-foreground hover:bg-secondary/80";
  return (
    <button onClick={() => trigger(job)} disabled={!!busy} data-testid={`run-${job}-button`}
      className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50 ${baseClass}`}>
      {isBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {isBusy ? `${label}...` : label}
    </button>
  );
}

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-bold mt-1">{value ?? "\u2014"}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function Overview({ trigger, busy }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { marketingApi.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, [busy]);
  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold">Pipeline status</h2>
        <div className="flex flex-wrap gap-2">
          <RunBtn label="Run full pipeline now" job="run_all" trigger={trigger} busy={busy} variant="primary" />
          <RunBtn label="Discover topics" job="fetch" trigger={trigger} busy={busy} />
          <RunBtn label="Score" job="score" trigger={trigger} busy={busy} />
          <RunBtn label="Generate" job="content" trigger={trigger} busy={busy} />
          <RunBtn label="Build SEO pages" job="articles" trigger={trigger} busy={busy} />
          <RunBtn label="Generate Images" job="images" trigger={trigger} busy={busy} />
          <RunBtn label="Regenerate All Images" job="regen_images" trigger={trigger} busy={busy} icon={RefreshCw} />
          <RunBtn label="Newsletter" job="newsletter" trigger={trigger} busy={busy} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard testid="stat-topics" label="Topics" value={stats?.topics_total} hint={`${stats?.topics_scored || 0} scored`} />
        <StatCard testid="stat-seo" label="SEO pages" value={stats?.seo_total} hint="published" />
        <StatCard testid="stat-scheduled" label="Scheduled" value={stats?.content_scheduled} hint="awaiting Buffer" />
        <StatCard testid="stat-published" label="Published" value={stats?.content_published} hint="shipped" />
      </div>
      <div className="bg-card border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border font-medium">Recent runs</div>
        <div className="divide-y divide-border text-sm" data-testid="recent-runs">
          {(stats?.recent_runs || []).map((r) => (
            <div key={r._id} className="px-4 py-2 flex items-center justify-between">
              <span>{r.run_type}</span>
              <span className="text-muted-foreground">{r.records_processed ?? 0} · {r.completed_at ? new Date(r.completed_at).toLocaleString() : ""}</span>
            </div>
          ))}
          {(stats?.recent_runs || []).length === 0 && (
            <div className="px-4 py-6 text-muted-foreground">No runs yet. Click "Run full pipeline now" to start.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Discovery({ busy }) {
  const [items, setItems] = useState(null);
  useEffect(() => { marketingApi.get("/topics").then((r) => setItems(r.data.items || [])).catch(() => {}); }, [busy]);
  return (
    <div className="space-y-4" data-testid="discovery-tab">
      <h2 className="text-lg font-semibold">Discovered topics</h2>
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Topic</th><th className="text-left p-3">Angle</th>
              <th className="text-left p-3">Audience</th><th className="text-left p-3">Intent</th><th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((t) => (
              <tr key={t._id} className="border-t border-border">
                <td className="p-3">{t.topic}<div className="text-xs text-muted-foreground mt-1">{t.target_keyword}</div></td>
                <td className="p-3 uppercase text-xs tracking-wider text-primary">{t.angle}</td>
                <td className="p-3 text-muted-foreground">{t.audience}</td>
                <td className="p-3">{t.buyer_intent_score ?? "\u2014"}</td>
                <td className="p-3 text-muted-foreground">{t.status}</td>
              </tr>
            ))}
            {(items || []).length === 0 && (
              <tr><td colSpan={5} className="p-6 text-muted-foreground text-center">No topics yet — run "Discover topics".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Content({ busy }) {
  const [items, setItems] = useState(null);
  const [platform, setPlatform] = useState("all");
  useEffect(() => {
    const q = platform === "all" ? "" : `?platform=${platform}`;
    marketingApi.get(`/content${q}`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [busy, platform]);
  function copy(s) { navigator.clipboard?.writeText(s); }
  return (
    <div className="space-y-4" data-testid="content-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Generated content</h2>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
          className="text-sm bg-background border border-input rounded-md px-2 py-1.5" data-testid="content-platform-filter">
          {["all", "instagram", "facebook", "seo", "newsletter"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {(items || []).map((c) => (
          <div key={c._id} className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-primary mb-1">{c.platform}</div>
            <div className="font-semibold mb-2">{c.headline}</div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{c.caption}</p>
            <div className="mt-3 flex gap-2 text-xs">
              <button onClick={() => copy(`${c.caption}${c.hashtags?.length ? "\n\n" + c.hashtags.map((h) => "#" + h).join(" ") : ""}`)}
                className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md">
                <Copy className="w-3 h-3" /> Copy
              </button>
              {c.platform !== "seo" && c.platform !== "newsletter" && (
                <a href={`${MARKETING_API_BASE}/og?id=${c._id}&template=${c.template || "how-to"}&download=1`}
                  className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md">
                  <Download className="w-3 h-3" /> Image
                </a>
              )}
            </div>
          </div>
        ))}
        {(items || []).length === 0 && (
          <div className="text-muted-foreground bg-card border border-border rounded-lg p-6 lg:col-span-2">No content yet — run "Generate".</div>
        )}
      </div>
    </div>
  );
}

function Publishing({ trigger, busy }) {
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  useEffect(() => { marketingApi.get("/publishing").then((r) => setData(r.data)).catch(() => {}); }, [busy]);
  async function testBuffer() {
    setDiagBusy(true); setDiag(null);
    try { const { data: d } = await marketingApi.get("/buffer-diagnostic"); setDiag(d); }
    catch (e) { setDiag({ ok: false, checks: [], warnings: [], next_steps: [e.message] }); }
    finally { setDiagBusy(false); marketingApi.get("/publishing").then((r) => setData(r.data)).catch(() => {}); }
  }
  return (
    <div className="space-y-6" data-testid="publishing-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Buffer publishing</h2>
          {data && (data.buffer_connected ? (
            <div className="text-xs text-emerald-600 mt-1" data-testid="buffer-status">Connected: {data.profiles?.map((p) => p.service).join(", ") || "no channels"}</div>
          ) : (
            <div className="text-xs text-amber-600 mt-1" data-testid="buffer-status">
              Buffer not connected{data.buffer_reason ? ` \u2014 ${data.buffer_reason}` : ""}.
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={testBuffer} disabled={diagBusy} data-testid="test-buffer-button"
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${diagBusy ? "animate-spin" : ""}`} /> {diagBusy ? "Running 15 checks..." : "Test Connection"}
          </button>
          <RunBtn label="Retry Failed" job="retry_failed" trigger={trigger} busy={busy} icon={AlertTriangle} />
          <button onClick={() => setConfirming("reimport")} disabled={!!busy}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Clear & re-import
          </button>
          <RunBtn label="Schedule next batch" job="schedule" trigger={trigger} busy={busy} icon={Send} variant="primary" />
        </div>
      </div>

      {diag && (
        <div data-testid="buffer-diag-result" className="bg-card border border-border rounded-lg overflow-hidden">
          <div className={`px-4 py-3 font-semibold text-sm ${diag.ok ? "bg-emerald-50 text-emerald-900 border-b border-emerald-200" : "bg-red-50 text-red-900 border-b border-red-200"}`}>
            {diag.ok ? "All 15 checks passed" : `${(diag.checks || []).filter(c => !c.ok).length} check(s) failed`}
          </div>
          <div className="divide-y divide-border text-xs">
            {(diag.checks || []).map((c, i) => (
              <div key={i} className={`px-4 py-2 flex items-start gap-2 ${!c.ok ? "bg-red-50/50" : ""}`}>
                <span className={`mt-0.5 font-bold ${c.ok ? "text-emerald-600" : "text-red-600"}`}>{c.ok ? "PASS" : "FAIL"}</span>
                <div>
                  <span className="font-medium">{c.name}</span>
                  {c.detail && <div className="text-muted-foreground mt-0.5 whitespace-pre-wrap">{c.detail}</div>}
                </div>
              </div>
            ))}
          </div>
          {(diag.warnings || []).length > 0 && (
            <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-xs">
              <div className="font-semibold text-amber-800 mb-1">Warnings</div>
              {diag.warnings.map((w, i) => <div key={i} className="text-amber-700">{w}</div>)}
            </div>
          )}
          {(diag.next_steps || []).length > 0 && (
            <div className="px-4 py-2 bg-blue-50 border-t border-blue-200 text-xs">
              <div className="font-semibold text-blue-800 mb-1">Next steps</div>
              {diag.next_steps.map((s, i) => <div key={i} className="text-blue-700">{s}</div>)}
            </div>
          )}
        </div>
      )}

      {data?.schedule && (
        <div className="bg-card border border-border rounded-lg p-4 text-xs">
          <div className="font-semibold mb-2">Posting schedule - timezone {data.schedule.timezone}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {Object.entries(data.schedule.slots || {}).map(([plat, slots]) => (
              <div key={plat} className="border border-border rounded-md p-2">
                <div className="uppercase tracking-wider text-[10px] text-primary font-semibold">{plat}</div>
                <div className="font-mono mt-1">
                  {slots.map((s, i) => (<div key={i}>{String(s.hour).padStart(2, "0")}:{String(s.minute).padStart(2, "0")} ({i === 0 ? "slot 1" : "slot 2"})</div>))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        {!data ? <div className="p-4">Loading...</div> : (data.queue || []).length === 0 ? (
          <div className="p-4 text-muted-foreground">Nothing in the queue.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground uppercase tracking-wider text-xs">
              <tr><th className="text-left p-3">Platform</th><th className="text-left p-3">Headline</th><th className="text-left p-3">Status</th><th className="text-left p-3">Scheduled</th></tr>
            </thead>
            <tbody>
              {(data.queue || []).map((q) => (
                <tr key={q._id} className="border-t border-border">
                  <td className="p-3 uppercase tracking-wider text-[10px] text-primary font-semibold">{q.platform}</td>
                  <td className="p-3">{q.headline}</td>
                  <td className="p-3 text-muted-foreground">{q.status}</td>
                  <td className="p-3 text-muted-foreground">{q.scheduled_at ? new Date(q.scheduled_at).toLocaleString() : "\u2014"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md shadow-xl">
            <div className="font-semibold text-lg mb-1">{confirming === "reimport" ? "Clear & re-import?" : "Clear Buffer queue?"}</div>
            <div className="text-sm text-muted-foreground mb-4">
              Permanently deletes every not-yet-published post on Buffer and resets local drafts.{" "}
              {confirming === "reimport" ? "Then schedules the next batch." : ""} Cannot be undone.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(null)} className="text-sm px-3 py-2 rounded-md border border-input">Cancel</button>
              <button onClick={() => { const j = confirming; setConfirming(null); trigger(j); }}
                className="text-sm px-3 py-2 rounded-md bg-destructive text-destructive-foreground">
                Yes, {confirming === "reimport" ? "clear & re-import" : "clear queue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Seo({ busy }) {
  const [items, setItems] = useState(null);
  useEffect(() => { marketingApi.get("/articles").then((r) => setItems(r.data.items || [])).catch(() => {}); }, [busy]);
  return (
    <div className="space-y-4" data-testid="seo-tab">
      <h2 className="text-lg font-semibold">Published SEO pages</h2>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {(items || []).length === 0 && <div className="p-4 text-muted-foreground">No pages yet — click "Build SEO pages" on Overview.</div>}
        {(items || []).map((p) => (
          <a key={p._id} href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`seo-page-${p.slug}`}
            className="block border-t border-border first:border-t-0 px-4 py-3 hover:bg-accent">
            <div className="font-semibold">{p.title}</div>
            <div className="text-xs text-muted-foreground mt-1">/blog/{p.slug} - {p.word_count} words - keyword: {p.target_keyword}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

function Newsletter({ busy }) {
  const [items, setItems] = useState(null);
  const [subStats, setSubStats] = useState(null);
  const [sending, setSending] = useState(null);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    marketingApi.get("/newsletter").then((r) => setItems(r.data.items || [])).catch(() => {});
    marketingApi.get("/admin/subscribers").then((r) => setSubStats(r.data)).catch(() => {});
  }, [busy]);

  const handleSend = async (id) => {
    if (!window.confirm("Send this newsletter to all active subscribers?")) return;
    setSending(id);
    setSendResult(null);
    try {
      const { data } = await marketingApi.post("/admin/send-newsletter", { newsletter_id: id });
      setSendResult({ id, ok: true, sent: data.sent, failed: data.failed });
    } catch (e) {
      setSendResult({ id, ok: false, error: e.response?.data?.error || e.message });
    } finally {
      setSending(null);
      marketingApi.get("/newsletter").then((r) => setItems(r.data.items || [])).catch(() => {});
    }
  };

  return (
    <div className="space-y-4" data-testid="newsletter-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Newsletter drafts</h2>
        {subStats && (
          <div className="text-sm text-muted-foreground" data-testid="subscriber-count">
            {subStats.active} active subscriber{subStats.active !== 1 ? "s" : ""}
          </div>
        )}
      </div>
      {sendResult && (
        <div className={`text-sm rounded-md p-3 border ${sendResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-red-300 bg-red-50 text-red-900"}`} data-testid="send-result">
          {sendResult.ok
            ? `Sent to ${sendResult.sent} subscriber${sendResult.sent !== 1 ? "s" : ""}${sendResult.failed ? ` (${sendResult.failed} failed)` : ""}.`
            : `Failed: ${sendResult.error}`}
        </div>
      )}
      {(items || []).length === 0 && <div className="p-4 text-muted-foreground bg-card border border-border rounded-lg">No drafts yet.</div>}
      {(items || []).map((n) => (
        <div key={n._id} className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Week of {new Date(n.week_start).toLocaleDateString()}</div>
            <div className="flex items-center gap-2">
              {n.status === "sent" ? (
                <span className="text-xs text-emerald-600 font-medium">Sent {n.sent_at ? new Date(n.sent_at).toLocaleDateString() : ""}</span>
              ) : (
                <button
                  onClick={() => handleSend(n._id)}
                  disabled={sending === n._id || !subStats?.active}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground font-medium disabled:opacity-50"
                  data-testid={`send-newsletter-${n._id}`}
                >
                  <Send className="w-3 h-3" /> {sending === n._id ? "Sending..." : "Send to subscribers"}
                </button>
              )}
            </div>
          </div>
          <p className="mt-2">{n.intro}</p>
          <div className="mt-4 space-y-3 text-sm">
            {(n.sections || []).map((s, i) => (
              <div key={i}><div className="font-semibold">{s.headline}</div><div className="text-muted-foreground whitespace-pre-wrap">{s.body}</div></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
